import React, { useState, useMemo } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Card,
  Divider,
  Typography,
  List,
  message,
  Row,
  Col,
  Badge,
  Space,
  Drawer,
  Checkbox,
  Spin,
} from "antd";
import {
  DeleteOutlined,
  ShoppingCartOutlined,
  MinusOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import {
  getProductCategoriesForBranch,
  getDeliveryMethods,
  createProductOrder,
  updateProductOrders,
  getDeliveryZones,
  validateCoupon,
  getTables,
} from "../../../service";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import styled from "styled-components";
import { colors } from "../../../styles/theme";
import { useBranchContext } from "../../../contexts/branch-context";
import { currencySymbol } from "../../../utils/currency";

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

interface CartItem {
  local_id: string;
  cart_positoin: {
    meal_name: string;
    meal_id: number;
    meal_quantity: number;
    meal_price: number;
    meal_options: any[];
    description?: string;
  };
}

interface CreateOrderModalProps {
  visible: boolean;
  onClose: () => void;
  selectedBranch: any;
  editMode?: boolean;
  existingOrder?: any;
}

const CategoryButton = styled(Button) <{ $active: boolean }>`
  width: 100%;
  text-align: left;
  white-space: normal;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: ${(props) => (props.$active ? "600" : "400")};
  background: ${(props) => (props.$active ? colors.primary : colors.white)};
  color: ${(props) => (props.$active ? colors.white : colors.textBlack)};
  border-color: ${(props) => (props.$active ? colors.primary : colors.cardBorder)};

  &:hover {
    background: ${(props) => (props.$active ? colors.primary : colors.backgroundLight)};
    color: ${(props) => (props.$active ? colors.white : colors.textBlack)};
    border-color: ${(props) => (props.$active ? colors.primary : colors.cardBorder)};
  }
`;

const ProductCard = styled(Card)`
  cursor: pointer;
  height: 140px;
  transition: all 0.3s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  .ant-card-body {
    padding: 16px;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
`;

const CartDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 16px;
    display: flex;
    flex-direction: column;
  }
`;

export const CreateOrderModal: React.FC<CreateOrderModalProps> = ({
  visible,
  onClose,
  selectedBranch,
  editMode = false,
  existingOrder = null,
}) => {
  const { t } = useTranslation();
  const { currentBranch } = useBranchContext();
  const curSymbol = currencySymbol(currentBranch?.currency);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [productModalVisible, setProductModalVisible] = useState(false);
  const [extensionsModalVisible, setExtensionsModalVisible] = useState(false);
  const [selectedProductForModal, setSelectedProductForModal] = useState<any>(null);
  const [selectedVariant, setSelectedVariant] = useState<any>(null);
  const [selectedExtensions, setSelectedExtensions] = useState<any[]>([]);
  const [productNote, setProductNote] = useState("");
  const [cartDrawerVisible, setCartDrawerVisible] = useState(false);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState<number | null>(null);
  const [showDeliveryMethodStep, setShowDeliveryMethodStep] = useState(false);
  const [showDeliveryZoneStep, setShowDeliveryZoneStep] = useState(false);
  const [selectedDeliveryZone, setSelectedDeliveryZone] = useState<any>(null);
  const [selectedTable, setSelectedTable] = useState<any>(null);
  const [deliveryPrice, setDeliveryPrice] = useState(0);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);

  // Calculate coupon discount on frontend
  const couponDiscount = useMemo(() => {
    if (!appliedCoupon) return 0;

    const isSpecificProducts = appliedCoupon.scope === 'SPECIFIC_PRODUCTS';
    const affectedProductIds = appliedCoupon.affectedProductIds || [];

    // Calculate total price for affected items only
    let affectedItemsTotal = 0;
    cart.forEach(item => {
      const productId = item.cart_positoin.meal_id;
      const itemPrice = item.cart_positoin.meal_price;
      const itemQuantity = item.cart_positoin.meal_quantity;

      if (!isSpecificProducts || (productId && affectedProductIds.includes(productId))) {
        affectedItemsTotal += itemPrice * itemQuantity;
      }
    });

    // Calculate discount based on coupon type
    if (affectedItemsTotal > 0) {
      if (appliedCoupon.type === 'PERCENTAGE') {
        return Math.round((affectedItemsTotal * appliedCoupon.value) / 100);
      } else {
        // FIXED_AMOUNT
        return Math.min(appliedCoupon.value, affectedItemsTotal);
      }
    }

    return 0;
  }, [appliedCoupon, cart]);

  // Fetch products by branch
  const { data: categories } = useQuery(
    ["product-categories", selectedBranch?.id],
    () => getProductCategoriesForBranch({ id: selectedBranch?.id }),
    {
      enabled: !!selectedBranch && visible,
    }
  );

  // Fetch delivery methods
  const { data: deliveryMethods, isLoading: deliveryMethodsLoading } = useQuery(
    ["delivery-methods", selectedBranch?.id],
    () => getDeliveryMethods({ id: selectedBranch?.id }),
    {
      enabled: !!selectedBranch && visible,
    }
  );

  // Fetch delivery zones
  const { data: deliveryZones, isLoading: deliveryZonesLoading } = useQuery(
    ["delivery-zones", selectedBranch?.id],
    () => getDeliveryZones({ id: selectedBranch?.id }),
    {
      enabled: !!selectedBranch && visible,
    }
  );

  // Fetch tables
  const { data: tables } = useQuery(
    ["tables", selectedBranch?.id],
    () => getTables(selectedBranch?.id),
    {
      enabled: !!selectedBranch && visible,
    }
  );
  console.log(tables)
  // Create order mutation
  const createOrderMutation = useMutation(createProductOrder, {
    onSuccess: () => {
      message.success(t("createOrderModal.orderCreatedSuccess"));
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return key === "product-orders" || key === "/product-orders";
        }
      });
      queryClient.invalidateQueries(["product-order-details"]);
      handleClose();
    },
    onError: (error: any) => {
      message.error(
        t("createOrderModal.orderCreateError", { message: error?.response?.data?.message || error.message })
      );
    },
  });

  // Update order mutation
  const updateOrderMutation = useMutation(
    (data: any) => updateProductOrders({ id: existingOrder?.id, body: data }),
    {
      onSuccess: () => {
        message.success(t("createOrderModal.orderUpdatedSuccess"));
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return key === "product-orders" || key === "/product-orders";
          }
        });
        queryClient.invalidateQueries(["product-order-details"]);
        handleClose();
      },
      onError: (error: any) => {
        message.error(
          t("createOrderModal.orderUpdateError", { message: error?.response?.data?.message || error.message })
        );
      },
    }
  );

  // Get products for selected category
  const productsForCategory = useMemo(() => {
    if (!selectedCategory || !categories) return [];
    const category = categories.find((c: any) => c.id === selectedCategory);
    return category?.productBases || [];
  }, [selectedCategory, categories]);

  // Calculate total price
  const totalPrice = useMemo(() => {
    return cart.reduce(
      (sum, item) =>
        sum + item.cart_positoin.meal_price * item.cart_positoin.meal_quantity,
      0
    );
  }, [cart]);

  // Group extensions by category from selected variant
  const groupedExtensions = useMemo(() => {
    if (!selectedVariant?.productExtensionVariant) return [];

    const grouped = selectedVariant.productExtensionVariant.reduce(
      (acc: any, extVariant: any) => {
        const categoryId = extVariant.productExtension?.category?.id;
        if (!categoryId) return acc;

        if (!acc[categoryId]) {
          acc[categoryId] = {
            id: categoryId,
            name: extVariant.productExtension.category.name,
            type: extVariant.productExtension.category.type,
            minimumRequiredElements: extVariant.productExtension.category.minimumRequiredElements || 0,
            maximumRequiredElements: extVariant.productExtension.category.maximumRequiredElements || 0,
            extensions: [],
          };
        }

        acc[categoryId].extensions.push({
          id: extVariant.productExtension.id,
          name: extVariant.productExtension.name,
          productExtensionVariant: [
            {
              id: extVariant.id,
              priceExtension: extVariant.priceExtension,
            },
          ],
        });

        return acc;
      },
      {}
    );

    return Object.values(grouped);
  }, [selectedVariant]);

  // Select first category on load
  React.useEffect(() => {
    if (categories && categories.length > 0 && !selectedCategory) {
      setSelectedCategory(categories[0].id);
    }
  }, [categories, selectedCategory]);

  // Initialize form with existing order data in edit mode
  React.useEffect(() => {
    if (editMode && existingOrder && visible && deliveryZones) {
      // Load cart from existing order
      setCart(existingOrder.details?.cart || []);

      // Pre-fill form with existing customer data
      form.setFieldsValue({
        clientName: existingOrder.clientName,
        phoneNumber: existingOrder.telNumber,
        email: existingOrder.email,
        address: existingOrder.details?.deliveryAddress || "",
        apartmentNumber: existingOrder.details?.apertmenNumber || "",
        deliveryMethod: existingOrder.deliveryMethodId,
        deliveryNote: existingOrder.details?.deliveryNote,
        marketingConsent: existingOrder.details?.marketingConsent || false,
        isPaid: existingOrder.details?.paymentsType === "OFFLINE_PAID",
      });

      // Set selected delivery method for button highlighting
      setSelectedDeliveryMethod(existingOrder.deliveryMethodId);

      // Set delivery price from existing order
      const existingDeliveryPrice = existingOrder.details?.deliveryPrice || 0;
      setDeliveryPrice(existingDeliveryPrice);

      // Find and set the selected delivery zone based on existing order's deliveryZone name
      if (existingOrder.details?.deliveryZone && deliveryZones) {
        const matchingZone = deliveryZones.find(
          (zone: any) => zone.name === existingOrder.details.deliveryZone
        );
        if (matchingZone) {
          setSelectedDeliveryZone(matchingZone);
        }
      }

      // Find and set the selected table
      if (tables) {
        if (existingOrder.details?.tableId) {
          const matchingTable = tables.find((t: any) => t.id === existingOrder.details.tableId);
          if (matchingTable) setSelectedTable(matchingTable);
        } else if (existingOrder.details?.tableName) {
          const matchingTable = tables.find((t: any) => t.name === existingOrder.details.tableName);
          if (matchingTable) setSelectedTable(matchingTable);
        }
      }

      // Don't auto-navigate to checkout - let user modify products
      setShowCheckout(false);
    }
  }, [editMode, existingOrder, visible, form, deliveryZones, tables]);

  // Handle product click - open modal with variants/extensions
  const handleProductClick = (product: any) => {
    setSelectedProductForModal(product);

    // Auto-select variant if there's only one
    if (product.productBaseVariants && product.productBaseVariants.length === 1) {
      setSelectedVariant(product.productBaseVariants[0]);
    } else {
      setSelectedVariant(null);
    }

    setSelectedExtensions([]);
    setProductNote("");
    setExtensionsModalVisible(false);
    setProductModalVisible(true);
  };

  // Add product to cart from modal
  const handleAddToCartFromModal = () => {
    if (!selectedProductForModal || !selectedVariant) {
      message.warning(t("createOrderModal.selectProductVariant"));
      return;
    }

    const variantName = selectedVariant.name ? ` ${selectedVariant.name}` : "";
    const meal_name = `${selectedProductForModal.name}${variantName}`;

    const meal_options: any[] = [];

    // Add variant info
    if (selectedVariant.name) {
      meal_options.push({
        sectionName: "variant",
        controls: [{ name: t("createOrderModal.variant"), value: selectedVariant.name }],
      });
    }

    // Calculate total price: variant price + extensions prices
    let totalPrice = selectedVariant.price;

    // Add extensions info and calculate their prices
    if (selectedExtensions.length > 0) {
      selectedExtensions.forEach((ext) => {
        const quantity = ext.quantity || 1;
        const extensionPrice = ext.productExtensionVariant?.[0]?.priceExtension || 0;

        // For QUANTITIES type, add with quantity info
        if (quantity > 1) {
          meal_options.push({
            sectionName: "additions",
            controls: [{ name: ext.name, value: `${quantity}x` }],
          });
          // Multiply price by quantity
          totalPrice += extensionPrice * quantity;
        } else {
          meal_options.push({
            sectionName: "additions",
            controls: [{ name: ext.name, value: "Tak" }],
          });
          totalPrice += extensionPrice;
        }
      });
    }

    const cartItem: CartItem = {
      local_id: uuidv4(),
      cart_positoin: {
        meal_name,
        meal_id: selectedProductForModal.id,
        meal_quantity: 1,
        meal_price: totalPrice,
        meal_options,
        description: productNote || undefined,
      },
    };

    setCart([...cart, cartItem]);
    setProductModalVisible(false);
    setExtensionsModalVisible(false);
    message.success(t("createOrderModal.addedToCart"));
  };

  // Remove item from cart
  const handleRemoveFromCart = (local_id: string) => {
    setCart(cart.filter((item) => item.local_id !== local_id));
  };

  // Update quantity
  const handleUpdateQuantity = (local_id: string, delta: number) => {
    setCart(
      cart.map((item) => {
        if (item.local_id === local_id) {
          const newQuantity = item.cart_positoin.meal_quantity + delta;
          if (newQuantity <= 0) return item; // Don't allow 0 or negative
          return {
            ...item,
            cart_positoin: {
              ...item.cart_positoin,
              meal_quantity: newQuantity,
            },
          };
        }
        return item;
      })
    );
  };

  // Go to checkout
  const handleGoToCheckout = () => {
    if (cart.length === 0) {
      message.warning(t("createOrderModal.addAtLeastOneProduct"));
      return;
    }

    // Show delivery method selection step
    setShowDeliveryMethodStep(true);
  };

  const handleContinueFromDeliveryMethod = () => {
    if (!selectedDeliveryMethod) {
      message.warning(t("createOrderModal.selectDeliveryMethod"));
      return;
    }

    const selectedMethod = deliveryMethods?.find((dm: any) => dm.id === selectedDeliveryMethod);

    const needsDeliveryZone = selectedMethod?.type === "TO_THE_ADDRESS" && deliveryZones && deliveryZones.length > 0;
    const isToTheTable = selectedMethod?.type === "TO_THE_TABLE";

    if (isToTheTable && !selectedTable) {
      message.warning(t("createOrderModal.selectTable"));
      return;
    }

    setShowDeliveryMethodStep(false);

    if (needsDeliveryZone) {
      setShowDeliveryZoneStep(true);
    } else {
      setShowCheckout(true);
    }
  };

  const handleBackFromDeliveryMethod = () => {
    setShowDeliveryMethodStep(false);
  };

  const handleSelectDeliveryZone = (zone: any) => {
    setSelectedDeliveryZone(zone);

    // Calculate delivery price based on freeAbove threshold
    const cartTotal = totalPrice;
    const isFreeDelivery = cartTotal >= zone.freeAbove;
    const calculatedDeliveryPrice = isFreeDelivery ? 0 : zone.price;

    setDeliveryPrice(calculatedDeliveryPrice);
  };

  const handleContinueFromDeliveryZone = () => {
    if (!selectedDeliveryZone) {
      message.warning(t("createOrderModal.selectDeliveryZone"));
      return;
    }
    setShowDeliveryZoneStep(false);
    setShowCheckout(true);
  };

  const handleBackFromDeliveryZone = () => {
    setShowDeliveryZoneStep(false);
    setShowDeliveryMethodStep(true);
  };

  const handleSelectTable = (table: any) => {
    setSelectedTable(table);
  };


  // Submit order
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields(["deliveryMethod"]);

      const deliveryMethod = deliveryMethods?.find(
        (dm: any) => dm.id === values.deliveryMethod
      );

      const clientName = form.getFieldValue("clientName") || "Klient";
      const phoneNumber = form.getFieldValue("phoneNumber") || "";
      const email = form.getFieldValue("email") || "";
      const address = form.getFieldValue("address") || "";
      const apartmentNumber = form.getFieldValue("apartmentNumber") || "";
      const marketingConsent = form.getFieldValue("marketingConsent") || false;
      const deliveryNote = form.getFieldValue("deliveryNote") || "";
      const isPaid = form.getFieldValue("isPaid") || false;

      // Calculate final price with delivery and coupon discount
      const finalPrice = totalPrice + deliveryPrice - couponDiscount;

      const orderData = {
        price: finalPrice,
        email,
        clientName,
        telNumber: phoneNumber,
        companyBranchId: selectedBranch.id,
        deliveryMethodId: values.deliveryMethod,
        status: editMode ? existingOrder?.status : "IN_QUEUE",
        isInternalOrder: true,
        details: {
          cart,
          payments: isPaid ? "Gotówka/Karta" : "Brak",
          paymentsType: isPaid ? "OFFLINE_PAID" : "OFFLINE",
          deliveryTimeType: editMode ? existingOrder?.details?.deliveryTimeType : "NOW",
          deliveryZone: selectedDeliveryZone?.name || (editMode ? existingOrder?.details?.deliveryZone : ""),
          deliveryLabel: deliveryMethod?.name || "",
          deliveryPrice: deliveryPrice,
          deliveryAddress: address,
          deliveryPlaceId: editMode ? existingOrder?.details?.deliveryPlaceId : "",
          orderDate: editMode ? existingOrder?.details?.orderDate : dayjs().format("YYYY-MM-DD HH:mm"),
          apertmenNumber: apartmentNumber,
          deliveryNote,
          deliveryType: deliveryMethod?.type || "SELF_PICKUP",
          additionalSections: editMode ? existingOrder?.details?.additionalSections : [],
          deliveryMethodLabel: deliveryMethod?.name || "",
          marketingConsent,
          couponCode: appliedCoupon ? appliedCoupon.code : undefined,
          tableName: selectedTable?.name || "",
          tableId: selectedTable?.id,
        },
      };

      if (editMode) {
        updateOrderMutation.mutate(orderData);
      } else {
        createOrderMutation.mutate(orderData);
      }
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setCart([]);
    setSelectedCategory(null);
    setShowCheckout(false);
    setProductModalVisible(false);
    setExtensionsModalVisible(false);
    setSelectedProductForModal(null);
    setCartDrawerVisible(false);
    setSelectedDeliveryMethod(null);
    setShowDeliveryMethodStep(false);
    setShowDeliveryZoneStep(false);
    setSelectedDeliveryZone(null);
    setDeliveryPrice(0);
    setCouponCode("");
    setAppliedCoupon(null);
    setSelectedTable(null);
    onClose();
  };

  const handleBackFromCheckout = () => {
    setShowCheckout(false);
  };

  // Apply coupon handler
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      message.warning(t("createOrderModal.enterCouponCode"));
      return;
    }

    try {
      const result = await validateCoupon({
        code: couponCode,
        companyBranchId: selectedBranch.id,
        cartTotal: totalPrice,
        productIds: cart.map((item) => item.cart_positoin.meal_id),
      });

      setAppliedCoupon(result.coupon);
      // Discount is now calculated automatically via useMemo
      message.success(t("createOrderModal.couponApplied"));
    } catch (error: any) {
      message.error(
        t("createOrderModal.couponValidationError", { message: error?.response?.data?.message || error.message })
      );
    }
  };

  // Remove coupon handler
  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    // Discount is now calculated automatically via useMemo
    setCouponCode("");
    message.info(t("createOrderModal.couponRemoved"));
  };

  // Get variants for selected product in modal
  const productVariants = useMemo(() => {
    if (!selectedProductForModal) return [];
    return selectedProductForModal.productBaseVariants || [];
  }, [selectedProductForModal]);

  // Render delivery method selection step
  const renderDeliveryMethodStep = () => {
    const selectedMethod = deliveryMethods?.find((dm: any) => dm.id === selectedDeliveryMethod);
    const isToTheTable = selectedMethod?.type === "TO_THE_TABLE";

    return (
      <div style={{ padding: "24px 0" }}>
        <div style={{ marginBottom: 24 }}>
          <Title level={5} style={{ marginBottom: 8 }}>
            {t("createOrderModal.selectDeliveryMethod")}
          </Title>
          <Text type="secondary">
            {t("createOrderModal.cartValue", { value: (totalPrice / 100).toFixed(2) })}
          </Text>
        </div>

        {deliveryMethodsLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" />
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "16px" }}>
            {deliveryMethods?.map((dm: any) => (
              <Button
                key={dm.id}
                size="large"
                type={selectedDeliveryMethod === dm.id ? "primary" : "default"}
                onClick={() => {
                  form.setFieldsValue({ deliveryMethod: dm.id });
                  setSelectedDeliveryMethod(dm.id);
                  // Reset delivery zone and table when changing delivery method
                  setSelectedDeliveryZone(null);
                  setSelectedTable(null);
                  setDeliveryPrice(0);
                }}
                style={{
                  height: "100px",
                  fontSize: "16px",
                  fontWeight: 500,
                  whiteSpace: "normal",
                  wordWrap: "break-word",
                  padding: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {dm.name}
              </Button>
            ))}
          </div>
        )}

        {isToTheTable && (
          <div style={{ marginTop: 32 }}>
            <div style={{ marginBottom: 16 }}>
              <Title level={5}>{t("createOrderModal.selectTable")}</Title>
            </div>
            {tables && tables.length > 0 ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "16px" }}>
                {tables.filter((t: any) => t.isActive).map((table: any) => (
                  <Button
                    key={table.id}
                    size="large"
                    type={selectedTable?.id === table.id ? "primary" : "default"}
                    onClick={() => handleSelectTable(table)}
                    style={{
                      height: "80px",
                      fontSize: "16px",
                      padding: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {table.name}
                  </Button>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <Text type="secondary">{t("createOrderModal.noAvailableTables")}</Text>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render delivery zone selection step
  const renderDeliveryZoneStep = () => {
    const selectedMethod = deliveryMethods?.find((dm: any) => dm.id === selectedDeliveryMethod);

    return (
      <div style={{ padding: "24px 0" }}>
        <div style={{ marginBottom: 24 }}>
          <Title level={5} style={{ marginBottom: 8 }}>
            {t("createOrderModal.selectDeliveryZone")}
          </Title>
          <Text type="secondary">
            {t("createOrderModal.deliveryMethodLabel", { name: selectedMethod?.name })}
          </Text>
        </div>

        {deliveryZonesLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" />
          </div>
        ) : deliveryZones && deliveryZones.length > 0 ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "16px" }}>
            {deliveryZones.map((zone: any) => {
              const isFreeDelivery = totalPrice >= zone.freeAbove;
              const isSelected = selectedDeliveryZone?.id === zone.id;

              return (
                <Button
                  key={zone.id}
                  size="large"
                  type={isSelected ? "primary" : "default"}
                  onClick={() => handleSelectDeliveryZone(zone)}
                  style={{
                    height: "auto",
                    padding: "16px",
                    textAlign: "left",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    whiteSpace: "normal",
                  }}
                >
                  <Text strong style={{ fontSize: 16, marginBottom: 8, color: isSelected ? "var(--color-text-inverse)" : "inherit" }}>
                    {zone.name}
                  </Text>

                  <div style={{ width: "100%", marginTop: 8 }}>
                    {isFreeDelivery ? (
                      <Text style={{ fontSize: 14, fontWeight: 600, color: isSelected ? "var(--color-text-inverse)" : "var(--color-success)" }}>
                        {t("createOrderModal.freeDelivery")}
                      </Text>
                    ) : (
                      <>
                        <Text style={{ fontSize: 14, fontWeight: 600, color: isSelected ? "var(--color-text-inverse)" : "inherit" }}>
                          {(zone.price / 100).toFixed(2)} {curSymbol}
                        </Text>
                        {zone.freeAbove > 0 && (
                          <div style={{ marginTop: 4 }}>
                            <Text style={{ fontSize: 12, color: isSelected ? "var(--color-text-inverse)" : "var(--color-text-secondary)" }}>
                              {t("createOrderModal.freeFrom", { value: (zone.freeAbove / 100).toFixed(2) })}
                            </Text>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {zone.minimumOrderValue > 0 && totalPrice < zone.minimumOrderValue && (
                    <div style={{ marginTop: 8, width: "100%" }}>
                      <Text style={{ fontSize: 12, color: isSelected ? "var(--color-text-inverse)" : "var(--color-error)" }}>
                        {t("createOrderModal.minOrder", { value: (zone.minimumOrderValue / 100).toFixed(2) })}
                      </Text>
                    </div>
                  )}
                </Button>
              );
            })}
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Text type="secondary">{t("createOrderModal.noAvailableDeliveryZones")}</Text>
          </div>
        )}

        {selectedDeliveryZone && (
          <div style={{ marginTop: 24, padding: 16, background: "var(--color-background)", borderRadius: 6 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <Text strong>{t("createOrderModal.summary")}</Text>
                <div style={{ marginTop: 8 }}>
                  <Text>{t("createOrderModal.cartValue", { value: (totalPrice / 100).toFixed(2) })}</Text>
                </div>
                <div style={{ marginTop: 4 }}>
                  <Text>{t("createOrderModal.deliveryCost")}: {deliveryPrice === 0 ? t("createOrderModal.free") : `${(deliveryPrice / 100).toFixed(2)} ${curSymbol}`}</Text>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <Text type="secondary" style={{ fontSize: 12 }}>{t("createOrderModal.total")}</Text>
                <div>
                  <Text strong style={{ fontSize: 20 }}>
                    {((totalPrice + deliveryPrice) / 100).toFixed(2)} {curSymbol}
                  </Text>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTableStep = () => {
    return (
      <div style={{ padding: "24px 0" }}>
        <div style={{ marginBottom: 24 }}>
          <Title level={5} style={{ marginBottom: 8 }}>
            {t("createOrderModal.selectTable")}
          </Title>
        </div>
        {tables && tables.length > 0 ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "16px" }}>
            {tables.filter((t: any) => t.isActive).map((table: any) => (
              <Button
                key={table.id}
                size="large"
                onClick={() => handleSelectTable(table)}
                style={{
                  height: "80px",
                  fontSize: "16px",
                  padding: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {table.name}
              </Button>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Text type="secondary">{t("createOrderModal.noAvailableTables")}</Text>
            <Button onClick={() => {
              // Skip table selection if no tables
              setShowDeliveryMethodStep(false);
              setShowCheckout(true);
            }}>
              {t("createOrderModal.skipTableSelection")}
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderPOSInterface = () => (
    <div style={{ display: "flex", height: "70vh" }}>
      {/* Categories - Left Side */}
      <div
        style={{
          width: "200px",
          borderRight: `1px solid ${colors.backgroundLight}`,
          paddingRight: "8px",
          overflowY: "auto",
        }}
      >
        <Title level={5} style={{ marginBottom: 16 }}>
          {t("createOrderModal.categories")}
        </Title>
        {categories?.map((category: any) => (
          <CategoryButton
            key={category.id}
            $active={selectedCategory === category.id}
            onClick={() => setSelectedCategory(category.id)}
          >
            {category.name}
          </CategoryButton>
        ))}
      </div>

      {/* Products - Right Side */}
      <div style={{ flex: 1, paddingLeft: "16px", overflowY: "auto" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <Title level={5} style={{ margin: 0 }}>
            {t("createOrderModal.products")}
          </Title>
          <Badge count={cart.length} showZero offset={[-74, 20]}>
            <Button
              icon={<ShoppingCartOutlined />}
              onClick={() => setCartDrawerVisible(true)}
            >
              {t("createOrderModal.cart")}
            </Button>
          </Badge>
        </div>

        <Row gutter={[16, 16]}>
          {productsForCategory.map((product: any) => (
            <Col key={product.id} xs={24} sm={12} lg={8}>
              <ProductCard onClick={() => handleProductClick(product)}>
                <div>
                  <Text strong style={{ fontSize: 16 }}>
                    {product.name}
                  </Text>
                  {product.description && (
                    <div>
                      <Paragraph
                        type="secondary"
                        style={{ fontSize: 12, marginBottom: 0 }}
                        ellipsis={{ rows: 2 }}
                      >
                        {product.description}
                      </Paragraph>
                    </div>
                  )}
                </div>
                <div style={{ marginTop: 8 }}>
                  {product.productBaseVariants?.[0] && (
                    <Text strong style={{ fontSize: 14, color: colors.primary }}>
                      {t("createOrderModal.fromPrice", { value: (product.productBaseVariants[0].price / 100).toFixed(2) })}
                    </Text>
                  )}
                </div>
              </ProductCard>
            </Col>
          ))}
        </Row>

        {productsForCategory.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Text type="secondary">{t("createOrderModal.noProductsInCategory")}</Text>
          </div>
        )}
      </div>
    </div>
  );



  // Render checkout form
  const renderCheckoutForm = () => {
    const selectedMethod = deliveryMethods?.find((dm: any) => dm.id === selectedDeliveryMethod);

    return (
      <Form form={form} layout="vertical">
        <Title level={4}>{t("createOrderModal.orderSummary")}</Title>

        <Divider />

        <List
          dataSource={cart}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Text strong>
                  {((item.cart_positoin.meal_price * item.cart_positoin.meal_quantity) / 100).toFixed(2)} {curSymbol}
                </Text>,
              ]}
            >
              <List.Item.Meta
                title={`${item.cart_positoin.meal_quantity}x ${item.cart_positoin.meal_name}`}
                description={
                  <>
                    {item.cart_positoin.description && (
                      <div>{item.cart_positoin.description}</div>
                    )}
                    {/* Display selected extensions */}
                    {item.cart_positoin.meal_options
                      ?.filter((opt: any) => opt.sectionName === "additions")
                      .map((opt: any, idx: number) => (
                        <div key={idx} style={{ marginTop: 2 }}>
                          {opt.controls?.map((ctrl: any, ctrlIdx: number) => (
                            <div key={ctrlIdx} style={{ fontSize: 12 }}>
                              + {ctrl.name}
                              {ctrl.value !== "Tak" && ctrl.value ? ` (${ctrl.value})` : ""}
                            </div>
                          ))}
                        </div>
                      ))}
                  </>
                }
              />
            </List.Item>
          )}
        />

        <Divider />

        {/* Delivery Information Section */}
        {selectedMethod && (
          <div style={{ marginBottom: 24, padding: 16, background: "var(--color-background)", borderRadius: 6 }}>
            <Text strong style={{ fontSize: 14, display: "block", marginBottom: 8 }}>
              {t("createOrderModal.deliveryInformation")}
            </Text>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text type="secondary">{t("createOrderModal.deliveryMethodColon")}</Text>
                <Text>{selectedMethod.name}</Text>
              </div>
              {selectedDeliveryZone && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text type="secondary">{t("createOrderModal.deliveryZoneColon")}</Text>
                  <Text>{selectedDeliveryZone.name}</Text>
                </div>
              )}
              {selectedTable && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text type="secondary">{t("createOrderModal.tableColon")}</Text>
                  <Text>{selectedTable.name}</Text>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text type="secondary">{t("createOrderModal.deliveryCostColon")}</Text>
                <Text strong>
                  {deliveryPrice === 0 ? t("createOrderModal.free") : `${(deliveryPrice / 100).toFixed(2)} ${curSymbol}`}
                </Text>
              </div>
            </div>
          </div>
        )}

        <div style={{ textAlign: "right", marginBottom: 24 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
            <div>
              <Text type="secondary">{t("createOrderModal.productsLabel")} </Text>
              <Text strong>{(totalPrice / 100).toFixed(2)} {curSymbol}</Text>
            </div>
            {deliveryPrice > 0 && (
              <div>
                <Text type="secondary">{t("createOrderModal.deliveryLabel")} </Text>
                <Text strong>{(deliveryPrice / 100).toFixed(2)} {curSymbol}</Text>
              </div>
            )}
            {couponDiscount > 0 && (
              <div>
                <Text type="secondary">{t("createOrderModal.discountLabel")} </Text>
                <Text strong style={{ color: "var(--color-success)" }}>-{(couponDiscount / 100).toFixed(2)} {curSymbol}</Text>
              </div>
            )}
            <Divider style={{ margin: "8px 0" }} />
            <div>
              <Title level={3} style={{ margin: 0 }}>
                {t("createOrderModal.totalAmount", { value: ((totalPrice + deliveryPrice - couponDiscount) / 100).toFixed(2) })}
              </Title>
            </div>
          </div>
        </div>

        <Divider orientation="left">{t("createOrderModal.discountCouponOptional")}</Divider>

        {appliedCoupon ? (
          <div style={{ marginBottom: 24, padding: 16, background: "var(--color-success-light)", borderRadius: 6, border: "1px solid var(--color-success)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <Text strong style={{ color: "var(--color-success)", fontSize: 16 }}>
                  {appliedCoupon.code}
                </Text>
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t("createOrderModal.discountValue", { value: (couponDiscount / 100).toFixed(2) })}
                  </Text>
                </div>
              </div>
              <Button
                type="text"
                danger
                onClick={handleRemoveCoupon}
              >
                {t("createOrderModal.remove")}
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: 24 }}>
            <Space.Compact style={{ width: "100%" }}>
              <Input
                size="large"
                placeholder={t("createOrderModal.enterCouponCodePlaceholder")}
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                onPressEnter={handleApplyCoupon}
              />
              <Button
                type="primary"
                size="large"
                onClick={handleApplyCoupon}
              >
                {t("createOrderModal.apply")}
              </Button>
            </Space.Compact>
          </div>
        )}

        <Divider orientation="left">{t("createOrderModal.customerDataOptional")}</Divider>

        <Form.Item name="clientName" label={t("createOrderModal.fullName")}>
          <Input size="large" placeholder={t("createOrderModal.fullNamePlaceholder")} />
        </Form.Item>

        <Form.Item name="phoneNumber" label={t("createOrderModal.phoneNumber")}>
          <Input size="large" placeholder={t("createOrderModal.phoneNumberPlaceholder")} />
        </Form.Item>

        <Form.Item name="email" label={t("createOrderModal.email")}>
          <Input size="large" type="email" placeholder={t("createOrderModal.emailPlaceholder")} />
        </Form.Item>

        <Form.Item name="address" label={t("createOrderModal.address")}>
          <Input size="large" placeholder={t("createOrderModal.addressPlaceholder")} />
        </Form.Item>

        <Form.Item name="apartmentNumber" label={t("createOrderModal.apartmentNumber")}>
          <Input size="large" placeholder={t("createOrderModal.apartmentNumberPlaceholder")} />
        </Form.Item>

        <Form.Item name="marketingConsent" valuePropName="checked">
          <Checkbox>
            {t("createOrderModal.marketingConsent")}
          </Checkbox>
        </Form.Item>

        <Divider orientation="left">{t("createOrderModal.payment")}</Divider>

        <Form.Item name="isPaid" valuePropName="checked" initialValue={false}>
          <Checkbox>
            {t("createOrderModal.orderPaid")}
          </Checkbox>
        </Form.Item>

        <Divider orientation="left">{t("createOrderModal.notes")}</Divider>

        <Form.Item name="deliveryNote" label={t("createOrderModal.orderNotes")}>
          <TextArea size="large" rows={3} placeholder={t("createOrderModal.orderNotesPlaceholder")} />
        </Form.Item>
      </Form>
    );
  };

  return (
    <>
      <Modal
        title={
          editMode
            ? t("createOrderModal.editOrderTitle")
            : showDeliveryMethodStep
              ? t("createOrderModal.selectDeliveryMethod")
              : showDeliveryZoneStep
                ? t("createOrderModal.selectDeliveryZone")
                : showCheckout
                  ? t("createOrderModal.finalizeOrderTitle")
                  : t("createOrderModal.newOrderPosTitle")
        }
        open={visible}
        onCancel={handleClose}
        width={showCheckout || showDeliveryZoneStep || showDeliveryMethodStep ? 600 : 1200}
        footer={
          showDeliveryMethodStep
            ? [
              <Button key="back" size="large" onClick={handleBackFromDeliveryMethod}>
                {t("createOrderModal.back")}
              </Button>,
              <Button
                key="continue"
                size="large"
                type="primary"
                onClick={handleContinueFromDeliveryMethod}
                disabled={!selectedDeliveryMethod}
              >
                {t("createOrderModal.continue")}
              </Button>,
            ]
            : showDeliveryZoneStep
              ? [
                <Button key="back" size="large" onClick={handleBackFromDeliveryZone}>
                  {t("createOrderModal.back")}
                </Button>,
                <Button
                  key="continue"
                  size="large"
                  type="primary"
                  onClick={handleContinueFromDeliveryZone}
                  disabled={!selectedDeliveryZone}
                >
                  {t("createOrderModal.continue")}
                </Button>,
              ]
              : showCheckout
                ? [
                  <Button key="back" size="large" onClick={handleBackFromCheckout}>
                    {t("createOrderModal.backToProducts")}
                  </Button>,
                  <Button
                    key="submit"
                    size="large"
                    type="primary"
                    loading={createOrderMutation.isLoading || updateOrderMutation.isLoading}
                    onClick={handleSubmit}
                  >
                    {editMode ? t("createOrderModal.saveChanges") : t("createOrderModal.placeOrder")}
                  </Button>,
                ]
                : [
                  <Button key="cancel" size="large" onClick={handleClose}>
                    {t("createOrderModal.cancel")}
                  </Button>,
                  <Button
                    key="checkout"
                    size="large"
                    type="primary"
                    onClick={handleGoToCheckout}
                    disabled={cart.length === 0}
                  >
                    {t("createOrderModal.goToSummaryCount", { count: cart.length })}
                  </Button>,
                ]
        }
      >
        {showDeliveryMethodStep
          ? renderDeliveryMethodStep()
          : showDeliveryZoneStep
            ? renderDeliveryZoneStep()
            : showCheckout
              ? renderCheckoutForm()
              : renderPOSInterface()}
      </Modal>

      {/* Product Details Modal */}
      <Modal
        title={selectedProductForModal?.name}
        open={productModalVisible}
        onCancel={() => setProductModalVisible(false)}
        onOk={handleAddToCartFromModal}
        okText={t("createOrderModal.addToCart")}
        okButtonProps={{ size: "large" }}
        cancelButtonProps={{ size: "large" }}
        cancelText={t("createOrderModal.cancel")}
        width={500}
      >
        {selectedProductForModal && (
          <div>
            {selectedProductForModal.description && (
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary">{selectedProductForModal.description}</Text>
              </div>
            )}

            <Divider orientation="left">{t("createOrderModal.selectVariant")}</Divider>
            <Space direction="vertical" style={{ width: "100%" }}>
              {productVariants.map((variant: any) => (
                <Button
                  key={variant.id}
                  size="large"
                  type={selectedVariant?.id === variant.id ? "primary" : "default"}
                  block
                  onClick={() => setSelectedVariant(variant)}
                  style={{ textAlign: "left", height: "auto", padding: "12px" }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span>{variant.name || t("createOrderModal.standard")}</span>
                    <span style={{ fontWeight: "bold" }}>
                      {(variant.price / 100).toFixed(2)} {curSymbol}
                    </span>
                  </div>
                </Button>
              ))}
            </Space>

            {/* Render extension categories button */}
            <div style={{ marginTop: 24 }}>
              <Button
                block
                size="large"
                onClick={() => setExtensionsModalVisible(true)}
                style={{
                  height: "auto",
                  padding: "16px",
                  borderColor: colors.primary,
                  color: colors.primary,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}
              >
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <span style={{ fontWeight: 600 }}>
                    {groupedExtensions.length > 0 ? t("createOrderModal.customizeAddons") : t("createOrderModal.addNotes")}
                  </span>
                  {selectedExtensions.length > 0 && groupedExtensions.length > 0 && (
                    <Badge count={selectedExtensions.length} style={{ backgroundColor: colors.primary }} />
                  )}
                  {productNote && groupedExtensions.length === 0 && (
                    <Badge dot style={{ backgroundColor: colors.primary }} />
                  )}
                </div>
              </Button>
              {selectedExtensions.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t("createOrderModal.selected", { value: selectedExtensions.map((e: any) => e.name).join(", ") })}
                  </Text>
                </div>
              )}
              {productNote && (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t("createOrderModal.notesValue", { value: productNote })}
                  </Text>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Extensions (Add-ons) Modal */}
      <Modal
        title={t("createOrderModal.addonsTitle", { name: selectedProductForModal?.name || '' })}
        open={extensionsModalVisible}
        onCancel={() => setExtensionsModalVisible(false)}
        width={500}
        footer={[
          <Button key="back" size="large" onClick={() => setExtensionsModalVisible(false)}>
            {t("createOrderModal.back")}
          </Button>,
          <Button
            key="add"
            type="primary"
            size="large"
            onClick={handleAddToCartFromModal}
          >
            {t("createOrderModal.addToCart")}
          </Button>
        ]}
      >
        <div style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 8 }}>
          {/* Render extension categories (additions) */}
          {groupedExtensions.length > 0 ? (
            groupedExtensions.map((extCategory: any) => (
              <div key={extCategory.id}>
                <Divider orientation="left">
                  {extCategory.name}
                  {extCategory.minimumRequiredElements > 0 && (
                    <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                      {t("createOrderModal.minElements", { count: extCategory.minimumRequiredElements })}
                    </Text>
                  )}
                </Divider>

                {extCategory.type === "MULTISELECT" && (
                  <Space direction="vertical" style={{ width: "100%" }}>
                    {extCategory.extensions?.map((ext: any) => (
                      <Button
                        key={ext.id}
                        type={selectedExtensions.some((e: any) => e.id === ext.id) ? "primary" : "default"}
                        block
                        size="large"
                        onClick={() => {
                          const isSelected = selectedExtensions.some((e: any) => e.id === ext.id);
                          if (isSelected) {
                            setSelectedExtensions(
                              selectedExtensions.filter((item: any) => item.id !== ext.id)
                            );
                          } else {
                            setSelectedExtensions([...selectedExtensions, ext]);
                          }
                        }}
                        style={{ textAlign: "left", padding: "12px 16px", height: "auto" }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                          <span>{ext.name}</span>
                          {ext.productExtensionVariant?.[0]?.priceExtension > 0 && (
                            <span style={{ fontWeight: "bold" }}>
                              +{(ext.productExtensionVariant[0].priceExtension / 100).toFixed(2)} {curSymbol}
                            </span>
                          )}
                        </div>
                      </Button>
                    ))}
                  </Space>
                )}

                {extCategory.type === "SELECT" && (
                  <Space direction="vertical" style={{ width: "100%" }}>
                    {extCategory.extensions?.map((ext: any) => (
                      <Button
                        key={ext.id}
                        type={
                          selectedExtensions.some((e: any) => e.id === ext.id && e.categoryId === extCategory.id)
                            ? "primary"
                            : "default"
                        }
                        block
                        size="large"
                        onClick={() => {
                          // For SELECT type, remove any previous selection from this category
                          const filteredExtensions = selectedExtensions.filter(
                            (item: any) => item.categoryId !== extCategory.id
                          );
                          setSelectedExtensions([...filteredExtensions, { ...ext, categoryId: extCategory.id }]);
                        }}
                        style={{ textAlign: "left", padding: "8px 16px" }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                          <span>{ext.name}</span>
                          {ext.productExtensionVariant?.[0]?.priceExtension > 0 && (
                            <span style={{ fontWeight: "bold" }}>
                              +{(ext.productExtensionVariant[0].priceExtension / 100).toFixed(2)} {curSymbol}
                            </span>
                          )}
                        </div>
                      </Button>
                    ))}
                  </Space>
                )}

                {extCategory.type === "QUANTITIES" && (
                  <Space direction="vertical" style={{ width: "100%" }}>
                    {extCategory.extensions?.map((ext: any) => {
                      const selectedExt = selectedExtensions.find((e: any) => e.id === ext.id);
                      const quantity = selectedExt?.quantity || 0;

                      return (
                        <div
                          key={ext.id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "12px",
                            border: `1px solid ${colors.cardBorder}`,
                            borderRadius: "6px",
                            backgroundColor: quantity > 0 ? colors.infoLight : colors.white,
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 500 }}>{ext.name}</div>
                            {ext.productExtensionVariant?.[0]?.priceExtension > 0 && (
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                +{(ext.productExtensionVariant[0].priceExtension / 100).toFixed(2)} {curSymbol}
                              </Text>
                            )}
                          </div>
                          <Space>
                            <Button
                              type="default"
                              shape="circle"
                              size="large"
                              icon={<MinusOutlined />}
                              disabled={quantity === 0}
                              onClick={() => {
                                if (quantity === 1) {
                                  // Remove from selection
                                  setSelectedExtensions(
                                    selectedExtensions.filter((item: any) => item.id !== ext.id)
                                  );
                                } else if (quantity > 1) {
                                  // Decrease quantity
                                  setSelectedExtensions(
                                    selectedExtensions.map((item: any) =>
                                      item.id === ext.id
                                        ? { ...item, quantity: item.quantity - 1 }
                                        : item
                                    )
                                  );
                                }
                              }}
                            />
                            <Text strong style={{ minWidth: "30px", textAlign: "center" }}>
                              {quantity}
                            </Text>
                            <Button
                              type="primary"
                              shape="circle"
                              size="large"
                              icon={<PlusOutlined />}
                              onClick={() => {
                                if (quantity === 0) {
                                  // Add to selection with quantity 1
                                  setSelectedExtensions([
                                    ...selectedExtensions,
                                    { ...ext, quantity: 1 },
                                  ]);
                                } else {
                                  // Increase quantity
                                  setSelectedExtensions(
                                    selectedExtensions.map((item: any) =>
                                      item.id === ext.id
                                        ? { ...item, quantity: item.quantity + 1 }
                                        : item
                                    )
                                  );
                                }
                              }}
                            />
                          </Space>
                        </div>
                      );
                    })}
                  </Space>
                )}
              </div>
            ))
          ) : (
            <div style={{ padding: "16px 0", textAlign: "center" }}>
              <Text type="secondary">{t("createOrderModal.noAdditionalOptions")}</Text>
            </div>
          )}

          <Divider orientation="left">{t("createOrderModal.notes")}</Divider>
          <TextArea
            rows={3}
            size="large"
            placeholder={t("createOrderModal.productNotePlaceholder")}
            value={productNote}
            onChange={(e) => setProductNote(e.target.value)}
          />
        </div>
      </Modal>

      {/* Cart Drawer */}
      <CartDrawer
        title={t("createOrderModal.cart")}
        placement="right"
        onClose={() => setCartDrawerVisible(false)}
        open={cartDrawerVisible}
        width={400}
        footer={
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 18,
                fontWeight: "bold",
              }}
            >
              <span>{t("createOrderModal.totalColon")}</span>
              <span>{(totalPrice / 100).toFixed(2)} {curSymbol}</span>
            </div>
            <Button
              type="primary"
              size="large"
              block
              disabled={cart.length === 0}
              onClick={() => {
                setCartDrawerVisible(false);
                handleGoToCheckout();
              }}
            >
              {t("createOrderModal.goToSummary")}
            </Button>
          </div>
        }
      >
        <div style={{ flex: 1, overflowY: "auto" }}>
          {cart.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <ShoppingCartOutlined style={{ fontSize: 48, color: colors.cardBorder }} />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">{t("createOrderModal.cartIsEmpty")}</Text>
              </div>
            </div>
          ) : (
            <List
              dataSource={cart}
              renderItem={(item) => (
                <List.Item
                  style={{
                    flexDirection: "column",
                    alignItems: "stretch",
                    padding: "12px 0",
                    borderBottom: `1px solid ${colors.backgroundLight}`,
                  }}
                >
                  <div style={{ marginBottom: 8 }}>
                    <Text strong>{item.cart_positoin.meal_name}</Text>
                    {item.cart_positoin.description && (
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {item.cart_positoin.description}
                        </Text>
                      </div>
                    )}
                    {/* Display selected extensions */}
                    {item.cart_positoin.meal_options
                      ?.filter((opt: any) => opt.sectionName === "additions")
                      .map((opt: any, idx: number) => (
                        <div key={idx} style={{ marginTop: 4 }}>
                          {opt.controls?.map((ctrl: any, ctrlIdx: number) => (
                            <Text
                              key={ctrlIdx}
                              type="secondary"
                              style={{ fontSize: 11, display: "block" }}
                            >
                              + {ctrl.name}
                              {ctrl.value !== "Tak" && ctrl.value ? ` (${ctrl.value})` : ""}
                            </Text>
                          ))}
                        </div>
                      ))}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Space>
                      <Button
                        size="small"
                        icon={<MinusOutlined />}
                        onClick={() => handleUpdateQuantity(item.local_id, -1)}
                        disabled={item.cart_positoin.meal_quantity <= 1}
                      />
                      <Text strong>{item.cart_positoin.meal_quantity}</Text>
                      <Button
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={() => handleUpdateQuantity(item.local_id, 1)}
                      />
                    </Space>
                    <Space>
                      <Text strong>
                        {(
                          (item.cart_positoin.meal_price *
                            item.cart_positoin.meal_quantity) /
                          100
                        ).toFixed(2)}{" "}
                        {curSymbol}
                      </Text>
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveFromCart(item.local_id)}
                      />
                    </Space>
                  </div>
                </List.Item>
              )}
            />
          )}
        </div>
      </CartDrawer>
    </>
  );
};
